/* eslint-disable react/no-unknown-property */
import { useState, useEffect, useRef, ReactNode } from 'react'
import classnames from 'classnames/bind'
import videojs from 'video.js'
import ReactDOM from 'react-dom'

// Libs
import { getTime } from 'date-fns'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import useMedia from '@/hooks/useMedia'
import { useIsEmbedRoute } from '@/hooks/useIsEmbedRoute'
import { setSoundValue } from '@/redux/reducers/room'
import { handleGtmClickLiveRoomSetup } from '@/methods/handleGtmEvent'

// Components
import HostNotHere from '../HostNotHere'
import Volume from '@/components/shared/Volume'
import PlaybackProgress from '../PlaybackProgress'

// Constants
import { LIVE_SOURCE_TYPE } from '@/constants/liveRoom'

// Styles
import 'video.js/dist/video-js.css'
import styles from './style.module.scss'
import { trackLivePlayStart } from '@/methods/liveTracking'

const cx = classnames.bind(styles)

// 內嵌頁自動起播的重試間隔
const AUTO_PLAY_RETRY_INTERVAL = 1000

interface IVideoInfo {
  url: string
  beginTime: number
  endTime: number
  type: number
  userRole: string
}

interface IItem {
  videoList: IVideoInfo[]
  isShow?: boolean
  isFixed?: boolean
  type?: string
  controls?: boolean
  onTimeChange: (time: any, playStatus: number) => void
  bagComponent?: ReactNode
}

interface Playback {
  src: string
  playStatus: number
  currentTime: number
  totalTime: number
  duration: number
  startTime: number
  beginTime: number
  endTime: number
  offset: number
  videos: IVideoInfo[] | null
}

const Player = (props: IItem) => {
  const { videoList, controls = false, bagComponent } = props

  const roomData = useAppSelector((state) => state.room?.roomData)
  const soundValue = useAppSelector((state) => state.room.soundValue)
  const liveSourceType = useAppSelector((state) => state.room?.roomData.liveSourceType)
  const isLiveMP4 = liveSourceType === LIVE_SOURCE_TYPE.MP4

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentPlayer, setCurrentPlayer] = useState<any>(null)
  const [isMuted, setIsMuted] = useState(true)
  const [playback, setPlayback] = useState<Playback>({
    src: '',
    playStatus: 0,
    currentTime: 0,
    totalTime: 0,
    duration: 0,
    startTime: 0,
    beginTime: 0,
    endTime: 0,
    offset: 0,
    videos: null,
  })

  const dispatch = useAppDispatch()

  const videoRef = useRef<any>(null)
  const checkUnMutedRef = useRef(true)

  const roomActionContainer: any = typeof window === 'undefined' ? null : document.getElementById('roomAction')

  const { isMobile } = useMedia()
  const isEmbedRoute = useIsEmbedRoute()

  const handlePlayStatusChange = (status: number) => {
    if (!videoRef.current) return

    setPlayback({ ...playback, playStatus: status })
    props.onTimeChange(playback.currentTime, status)
    if (status === -1) {
      videoRef.current.pause()
    } else if (status === 1) {
      videoRef.current
        .play()
        .then(() => {
          if (currentPlayer && checkUnMutedRef.current) {
            currentPlayer.muted(false)
            dispatch(setSoundValue({ soundValue: 100 }))
          }
        })
        // play() 被 load 中斷或被自動播放政策擋下時會 reject；原本沒接會變成 unhandled rejection
        .catch(() => undefined)
    }

    //this.props.setPlaybackStatus(status)
    if (status === 0) {
      //this.props.setAllPointsInfo('currentTime', 0)
      // window.whiteboard.commands && window.whiteboard.commands.deleteAll()
      // this.props.deleteAllChats()
      // this.props.resetUserStar(0)
    }
  }

  const handleTimeChange = (time: number) => {
    const { offset } = playback
    time = Math.floor(time / 500) * 500
    //console.log('handleTimeChange.currentTime ===>', time, offset)
    let _currentTime = time / 1000 - offset
    isLiveMP4 && videoRef.current?.duration && (_currentTime = _currentTime % videoRef.current.duration)
    videoRef.current.currentTime = _currentTime
    setPlayback((prevPlayback) => ({
      ...prevPlayback,
      currentTime: time,
    }))
    props.onTimeChange(time, playback.playStatus)
  }

  const jump = (time: number) => {
    const { currentTime, totalTime } = playback
    let jumpTime = currentTime + time
    if (jumpTime <= 500) {
      jumpTime = 500
    }
    if (jumpTime >= totalTime) {
      jumpTime = totalTime
    }
    handleTimeChange(jumpTime)
  }

  const checkOffset = () => {
    const { currentTime } = playback
    //console.log('checkOffset ===>', currentTime, offset)
    handleTimeChange(currentTime)
  }

  const playbackProgressProps = {
    currentTime: playback.currentTime,
    // flipStamp: playback.flipStamp.map((item: any) => item.time),
    // starStamp: playback.starStamp,
    playStatus: playback.playStatus,
    totalTime: playback.totalTime,
    onCheckoutPlayStatus: handlePlayStatusChange,
    onChangeCurrent: handleTimeChange,
    // getSnapshotByTime: this.handleGetSnapshop,
    // ref: (ref) => (this.playbackProgress = ref),
    jump: jump,
  }

  const playTimerRef = useRef<any>(null)
  useEffect(() => {
    if (playback.playStatus === 1) {
      playTimerRef.current = setInterval(() => {
        if (playback.currentTime + 1000 > playback.totalTime) {
          handlePlayStatusChange(-1)
          handleTimeChange(0)
          return
        }

        const nowTime = playback.startTime + playback.currentTime
        if (playback.endTime < nowTime || playback.beginTime > nowTime) {
          const nextVideo = playback.videos?.find((a) => a.beginTime < nowTime && a.endTime > nowTime)
          if (nextVideo) {
            setIsPlaying(true)
            setPlayback((prevPlayback) => ({
              ...prevPlayback,
              src: nextVideo.url,
              beginTime: nextVideo.beginTime,
              endTime: nextVideo.endTime,
              offset: (nextVideo.beginTime - prevPlayback.startTime) / 1000,
            }))
          } else {
            setIsPlaying(false)
          }
        } else if (!isPlaying) {
          //console.log('===> playback.offset', playback.currentTime, playback.offset)
          let _currentTime = playback.currentTime / 1000 - playback.offset
          isLiveMP4 && videoRef.current?.duration && (_currentTime = _currentTime % videoRef.current.duration)
          videoRef.current.currentTime = _currentTime
          setIsPlaying(true)
        }

        setPlayback((prevPlayback) => ({
          ...prevPlayback,
          currentTime: prevPlayback.currentTime + 1000,
        }))
      }, 1000)
    } else if (playback.playStatus === -1) {
      clearInterval(playTimerRef.current)
      playTimerRef.current = null
    }

    return () => {
      clearInterval(playTimerRef.current)
    }
  }, [playback])

  useEffect(() => {
    if (!videoList) return
    const videos = videoList.slice().sort((a: IVideoInfo, b: IVideoInfo) => a.beginTime - b.endTime)
    //const totalTime = videos[videos.length - 1].endTime - videos[0].beginTime
    const start = getTime(new Date(roomData.liveTime ? roomData.liveTime : roomData.startTime))
    //const end = new Date(roomData.liveCloseTime ? roomData.liveCloseTime : roomData.endTime ?? 0)
    //const totalTime = differenceInMilliseconds(end, start) || 0
    const totalTime = videos[videos.length - 1].endTime - start

    setPlayback({
      ...playback,
      src: videos[0].url,
      totalTime: totalTime,
      videos: videos,
      startTime: start,
      beginTime: videos[0].beginTime,
      endTime: videos[0].endTime,
      offset: (videos[0].beginTime - start) / 1000,
    })
  }, [videoList])

  useEffect(() => {
    let player: any = null
    const src = playback.src

    const initPlayer = () => {
      if (currentPlayer) {
        currentPlayer.src({
          src: src,
          type: 'video/mp4',
        })
        setTimeout(() => {
          checkOffset()
        }, 100)
        currentPlayer.play()
        return
      }

      player = videojs(videoRef.current, {
        preload: 'auto',
        //autoplay: true,
        muted: true,
        controls,
        loop: isLiveMP4,
        sources: [
          {
            src,
            type: 'video/mp4',
          },
        ],
      })

      setCurrentPlayer(player)
      player.load()
      player.pause()

      player.on('play', () => {
        console.log('shop', 'Playback onPlay!')

        setTimeout(() => {
          setIsMuted(player?.muted())
        }, 100)
      })

      player.on('loadedmetadata', () => {
        console.log('shop', 'Playback loadedmetadata')
      })

      player.on('playing', () => {
        trackLivePlayStart() // 直播行為事件：每次進房記一次播放開始
        setTimeout(() => {
          checkUnMutedRef.current = false
        }, 500)
      })

      player.on('pause', () => {
        console.log('shop', 'Playback 暫停播放！')

        if (checkUnMutedRef.current) {
          checkUnMutedRef.current = false
          console.log('shop', 'HLS autoplay muted & replay')
          player.muted(true)
          player.play()
        }
      })

      player.on('error', (error: any) => {
        console.log('shop', 'Playback 播放錯誤！', error)
      })

      player.on('volumechange', () => {
        console.log('shop', 'volumechange')
      })
    }

    if (src) {
      initPlayer()
    }

    return () => {
      if (player) {
        //player.dispose()
      }
    }
  }, [playback.src, playback.offset])

  // 內嵌頁沒有播放控制列，回放要自己起播。
  //
  // 不能只在 currentPlayer 出現時起播一次：initPlayer 是 videojs(...) → load() → pause()，
  // load() 非同步，這時呼叫 play() 會被新的 load 請求中斷（AbortError）而靜默失敗。
  // 所以改成定期檢查 video 是否真的在播，沒在播就再試一次。
  //
  // 這個 interval 同時負責循環：播完後 playStatus 會被帶回 -1 且時間歸零，
  // 下一次檢查就會重新起播，正好是看板情境要的重複播放。
  useEffect(() => {
    if (!isEmbedRoute) return
    if (!currentPlayer || !playback.src) return

    const tryPlay = () => {
      // 已經在播就什麼都不做，避免蓋掉播放中的 playback 狀態
      if (!videoRef.current || !videoRef.current.paused) return

      handlePlayStatusChange(1)
    }

    tryPlay()

    const retryTimer = setInterval(tryPlay, AUTO_PLAY_RETRY_INTERVAL)

    return () => clearInterval(retryTimer)
  }, [isEmbedRoute, currentPlayer, playback.src])

  const handleSoundClick = () => {
    if (isMuted) {
      if (soundValue === 0) {
        dispatch(setSoundValue({ soundValue: 100 }))
      }
      handleGtmClickLiveRoomSetup({ isMuted: false })

      currentPlayer.muted(false)
      setIsMuted(false)
    } else {
      handleGtmClickLiveRoomSetup({ isMuted: true })
      currentPlayer.muted(true)
      setIsMuted(true)
    }
  }

  const handleSoundChange = (value: number) => {
    if (isMuted) {
      currentPlayer.muted(false)
      setIsMuted(false)
    }

    dispatch(setSoundValue({ soundValue: value }))

    currentPlayer.volume(value / 100)
  }

  return (
    <div className={cx('player')}>
      <div className={cx('player-item')} data-vjs-player>
        <video
          className={cx('player-item-video')}
          ref={videoRef}
          webkit-playsinline='true'
          playsInline={true}
          x5-playsinline='true'
          muted
          controls={controls}
        />
      </div>

      <div className={cx('player-action-wrapper')}>
        <Volume soundValue={soundValue} handleSoundClick={handleSoundClick} handleSoundChange={handleSoundChange} isFixed={false} isMuted={isMuted} />
      </div>

      {/* 內嵌頁不出播放進度條：那是快轉／拖曳用的，看板情境不需要，
          且桌機版是 portal 到直播間才有的 #roomAction 容器 */}

      {!isEmbedRoute &&
        !isMobile &&
        roomActionContainer &&
        ReactDOM.createPortal(<PlaybackProgress className={cx('progress')} {...playbackProgressProps} />, roomActionContainer)}
      {!isEmbedRoute && isMobile && (
        <div>
          <PlaybackProgress className={cx('progress', { hasBag: !!bagComponent })} {...playbackProgressProps} />
          {bagComponent}
        </div>
      )}

      {!isPlaying && <HostNotHere />}
    </div>
  )
}

export default Player
